import type { TPasswordGateError, TShareErrorVariant } from "../types/meetings";

// NOTE: Error codes returned by the backend on 410 Gone responses for shared chat links.
// Used by the viewer page to distinguish revoked vs expired links.
export const SHARE_ERROR_CODES = {
  REVOKED: "SHARE_REVOKED",
  EXPIRED: "SHARE_EXPIRED",
} as const;

// NOTE: Upper bound on how many recipients one specific-people share request
// may carry. Mirrors MAX_CHAT_SHARE_RECIPIENTS on the server
// (apps/nsai/server/src/chat/services/chat-share/chat-share.constants.ts),
// where it is enforced with @ArrayMaxSize on the create / add-recipients DTOs.
// The share picker caps its selection with it so an over-sized request is never
// submitted in the first place (NSAI-4538). Keep both copies in sync.
export const MAX_CHAT_SHARE_RECIPIENTS = 50;

// NOTE: React Query keys for the share state of one chat and for a shared
// chat's paginated messages. Shared so web and mobile write into the same cache
// entries — a mobile-local copy would silently diverge from web's invalidations.
export const CHAT_SHARE_QUERY_KEY = "chat-share";
export const SHARED_CHAT_QUERY_KEY = "shared-chat";
export const SHARED_CHAT_PAGE_SIZE = 20;

export const chatShareQueryKey = (chatUuid: string): string[] => [
  CHAT_SHARE_QUERY_KEY,
  chatUuid,
];

// NOTE: Path of the web viewer a share link points at. Mobile builds the same
// URL (against its configured web origin) so a link shared from a phone opens
// exactly where a link shared from the browser does.
export const SHARE_URL_PATH = "/shared/chat/";

// NOTE: NSAI-5823 — error codes the public (no-account) chat viewer switches on.
// Namespaced separately from SHARE_ERROR_CODES above because the two viewers are
// different pages with different recovery paths: an authenticated viewer signs
// in, a public one enters a password. Mirrors CHAT_PUBLIC_SHARE_ERROR_CODES on
// the server (apps/nsai/server/src/chat/constant/chat-public-share.constants.ts).
export const CHAT_PUBLIC_SHARE_ERROR_CODES = {
  NOT_FOUND: "CHAT_PUBLIC_SHARE_NOT_FOUND",
  REVOKED: "CHAT_PUBLIC_SHARE_REVOKED",
  EXPIRED: "CHAT_PUBLIC_SHARE_EXPIRED",
  INVALID_PASSWORD: "CHAT_PUBLIC_SHARE_INVALID_PASSWORD",
  // NOTE: The link is live but its password was regenerated after this token was
  // minted. Distinct from INVALID_PASSWORD: the viewer did nothing wrong, they
  // just need the new password.
  PASSWORD_ROTATED: "CHAT_PUBLIC_SHARE_PASSWORD_ROTATED",
} as const;

// NOTE: Header carrying the share-access token on every anonymous read. Keep in
// sync with CHAT_PUBLIC_SHARE_ACCESS_TOKEN_HEADER on the server. Deliberately a
// header and not a query param — a token in a URL lands in history, referrers
// and proxy logs.
export const CHAT_PUBLIC_SHARE_ACCESS_TOKEN_HEADER = "x-chat-share-token";

// NOTE: Path of the public viewer page. Distinct from SHARE_URL_PATH above:
// that page assumes a signed-in session, this one opens with a password prompt.
// Must match CHAT_PUBLIC_SHARE_VIEWER_PATH on the server.
export const PUBLIC_SHARE_URL_PATH = "/shared/public-chat/";

// NOTE: React Query keys for the owner's public link and for the anonymous
// viewer's paginated read. Shared so web and mobile write into the same cache
// entries rather than silently diverging on invalidation.
export const CHAT_PUBLIC_SHARE_QUERY_KEY = "chat-public-share";
export const PUBLIC_SHARED_CHAT_QUERY_KEY = "public-shared-chat";

export const chatPublicShareQueryKey = (chatUuid: string): string[] => [
  CHAT_PUBLIC_SHARE_QUERY_KEY,
  chatUuid,
];

// NOTE: The viewer's copy, keyed by outcome, shared so web and mobile cannot
// drift into telling the same person two different things about one link. Each
// outcome keeps its own wording on purpose: a mistyped link and one its owner
// deliberately turned off are different situations for whoever holds it — the
// first is worth re-checking, the second means asking for a new link. `generic`
// reuses the not-found body because an unclassifiable failure is, from the
// viewer's side, a link that does not work.
//
// Only the keys live here. The icon stays per app (lucide-react vs
// lucide-react-native resolve to different component types) and so does the
// layout, as with SIEM_DELIVERY_STATUS_COPY.
export const PUBLIC_CHAT_VIEWER_STATE_COPY = {
  not_found: {
    titleKey: "share.public_viewer.not_found_title",
    bodyKey: "share.public_viewer.not_found_body",
  },
  revoked: {
    titleKey: "share.public_viewer.revoked_title",
    bodyKey: "share.public_viewer.revoked_body",
  },
  expired: {
    titleKey: "share.public_viewer.expired_title",
    bodyKey: "share.public_viewer.expired_body",
  },
  generic: {
    titleKey: "share.public_viewer.generic_error",
    bodyKey: "share.public_viewer.not_found_body",
  },
} as const satisfies Record<
  TShareErrorVariant,
  { titleKey: string; bodyKey: string }
>;

// NOTE: One line per recoverable failure at the password gate. Collapsing them
// into "incorrect password" would tell a viewer whose password was rotated — or
// who simply typed too fast and got throttled — to fix something that is not
// wrong. Shared for the same reason as the map above.
export const PUBLIC_CHAT_PASSWORD_ERROR_COPY = {
  invalid: "share.public_viewer.invalid_password",
  password_rotated: "share.public_viewer.password_rotated",
  too_many_attempts: "share.public_viewer.too_many_attempts",
  generic_error: "share.public_viewer.generic_error",
} as const satisfies Record<TPasswordGateError, string>;
